import { plural } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { ItemGroup } from 'designSystem/components/Item'
import { Text } from 'designSystem/components/Text'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { getAgenticInlineDocumentCardItems } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/agenticInlineDocumentResults'
import type { DrawingRevisionOption } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type {
  DocChatResponseEvent,
  DocChatResponsePartialEvent,
} from 'features/knowledgeDocumentViewer/types'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { AgenticInlineDocumentResultCard } from './AgenticInlineDocumentResultCard'
import type { PreviewDocumentHandler } from './previewDocumentHandler'

const INITIAL_RESULT_COUNT = 3

const CollapsibleResults = styled(motion.div)`
  min-width: 0;
  overflow: hidden;
`

export function AgenticInlineDocumentResults({
  event,
  onPreviewDocument,
}: {
  event: DocChatResponseEvent | DocChatResponsePartialEvent
  onPreviewDocument: PreviewDocumentHandler
}) {
  const { t } = useLingui()
  const [isExpanded, setIsExpanded] = useState(false)
  const reduceMotion = useReducedMotion()
  const [selectedRevisionByCardKey, setSelectedRevisionByCardKey] = useState(
    () => new Map<string, DrawingRevisionOption>()
  )
  const { documentResults, documentRowsById, drawingRevisionGroupsMap } =
    useDocumentSearchData()
  const { selectedPipelineConfigId } = usePipelineConfig()

  const cardItems = useMemo(
    () =>
      getAgenticInlineDocumentCardItems({
        documentResults,
        documentRowsById,
        drawingRevisionGroupsMap,
        response: event,
        selectedPipelineConfigId,
      }),
    [
      documentResults,
      documentRowsById,
      drawingRevisionGroupsMap,
      event,
      selectedPipelineConfigId,
    ]
  )

  const initialItems = cardItems.slice(0, INITIAL_RESULT_COUNT)
  const additionalItems = cardItems.slice(INITIAL_RESULT_COUNT)
  const hiddenCount = cardItems.length - INITIAL_RESULT_COUNT
  const hasMore = hiddenCount > 0
  const documentCount = cardItems.length

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((expanded) => !expanded)
  }, [])

  const handleRevisionChange = useCallback(
    (cardKey: string, option: DrawingRevisionOption) => {
      setSelectedRevisionByCardKey((current) => {
        const next = new Map(current)
        next.set(cardKey, option)
        return next
      })
    },
    []
  )

  if (cardItems.length === 0 && !event.cmms) return null

  return (
    <Box
      asChild
      display='flex'
      direction='column'
      gap='sm'
      minWidth='none'
      width='full'
      paddingTop='xs'>
      <section aria-label={t`Retrieved sources`}>
        <Text as='p' variant='caption' color='muted'>
          {plural(documentCount, { one: '# source', other: '# sources' })}
        </Text>
        {event.cmms ? (
          <Text as='p' variant='caption' color='muted'>
            <Trans>
              Work orders come from an imported snapshot; results do not
              represent a complete maintenance history.
            </Trans>
          </Text>
        ) : null}
        {event.cmms?.truncated ? (
          <Text as='p' variant='caption' color='muted'>
            <Trans>
              Showing the top work-order matches. Narrow your question for more
              specific results.
            </Trans>
          </Text>
        ) : null}
        {event.cmms?.notes.length ? (
          <Text as='p' variant='caption' color='muted' role='status'>
            <Trans>
              Some search methods were unavailable or work-order evidence was
              shortened. Open a source to review the full imported record.
            </Trans>
          </Text>
        ) : null}
        <ItemGroup>
          {initialItems.map((item) => (
            <AgenticInlineDocumentResultCard
              key={item.key}
              documentRowsById={documentRowsById}
              item={item}
              onPreviewDocument={onPreviewDocument}
              onRevisionChange={handleRevisionChange}
              selectedRevision={selectedRevisionByCardKey.get(item.key)}
            />
          ))}
          <AnimatePresence initial={false}>
            {hasMore && isExpanded ? (
              <CollapsibleResults
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  duration: reduceMotion ? 0 : 0.18,
                  ease: 'easeOut',
                }}>
                <ItemGroup>
                  {additionalItems.map((item) => (
                    <AgenticInlineDocumentResultCard
                      key={item.key}
                      documentRowsById={documentRowsById}
                      item={item}
                      onPreviewDocument={onPreviewDocument}
                      onRevisionChange={handleRevisionChange}
                      selectedRevision={selectedRevisionByCardKey.get(item.key)}
                    />
                  ))}
                </ItemGroup>
              </CollapsibleResults>
            ) : null}
          </AnimatePresence>
        </ItemGroup>
        {hasMore ? (
          <Box display='flex'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              aria-expanded={isExpanded}
              onClick={handleToggleExpanded}>
              {isExpanded ? (
                <>
                  <Trans>Show less</Trans>{' '}
                  <ChevronUp aria-hidden data-icon='inline-end' />
                </>
              ) : (
                <>
                  <Trans>Show {hiddenCount} more</Trans>{' '}
                  <ChevronDown aria-hidden data-icon='inline-end' />
                </>
              )}
            </Button>
          </Box>
        ) : null}
      </section>
    </Box>
  )
}
